import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  categoriaLigaSchema,
  CategoriaLigaFormValues,
} from '../../../schemas/categoriaLiga/categoriaLigaSchema';
import { TipoPontuacao } from '../../../types/categoriaTorneio';
import type { CategoriaLiga } from '../../../types/categoriaLiga';
import { getTiebreakOptions, getValidTiebreakValues } from '../../../utils/tiebreakOptions';

// AC-2: rótulos em Português dos 8 valores do enum `NivelCategoria`, na ordem declarada
// do enum GraphQL (mesma ordem usada no select).
const NIVEL_LABELS: Record<string, string> = {
  A: 'A',
  B: 'B',
  C: 'C',
  D: 'D',
  E: 'E',
  INICIANTE: 'Iniciante',
  INTERMEDIARIO: 'Intermediário',
  PROFISSIONAL: 'Profissional',
};

// Rótulos em Português usados pelo atalho "por gênero" — mesmos valores do select de
// Gênero deste form.
const GENERO_LABELS: Record<string, string> = {
  MASCULINO: 'Masculino',
  FEMININO: 'Feminino',
  MISTO: 'Misto',
  OUTRO: 'Outro',
};

export interface CategoriaLigaFormProps {
  initialValues: CategoriaLigaFormValues;
  onSubmit: (values: CategoriaLigaFormValues) => Promise<void> | void;
  onCancel: () => void;
  /**
   * Demais `CategoriaLiga`s da mesma liga, já excluindo a categoria em edição —
   * filtragem é responsabilidade da PÁGINA chamadora (`LeagueCategoriaLigaForm.tsx`),
   * nunca deste componente (AC-16). Opcional: ausência (ex.: tela ainda carregando as
   * demais categorias) renderiza a seção de combináveis sem nenhum checkbox.
   */
  outrasCategorias?: CategoriaLiga[];
}

/**
 * Formulário da CategoriaLiga (RHF + Zod) — recebe `initialValues`/`onSubmit`/
 * `onCancel` por props, nenhuma chamada a service (mirror de `EditCategoryForm`).
 *
 * As regras de preenchimento espelham as da categoria de torneio (`EditCategoryForm`):
 *   - Grupos + Eliminação → Quantidade de Grupos + Classificados por Grupo (editáveis)
 *   - Grupo Único → Quantidade de Grupos = 1 e Classificados por Grupo = 0 (fixos)
 *   - Rei/Rainha → Tamanho de Grupo (4/8) + grupos fixos (1 / 0)
 *   - Super 8 → Tamanho de Grupo = 8 (fixo) + grupos fixos (1 / 0)
 *   - Eliminação Simples/Dupla → nenhum campo de grupo
 * "Tamanho de Grupo" só aparece em Rei/Rainha e Super 8; rodízio força Tipo = Solo.
 *
 * PRPs/plans/categoria-liga-phase-3-frontend.plan.md — Task 5 (Satisfies AC-A1).
 */
const CategoriaLigaForm: React.FC<CategoriaLigaFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  outrasCategorias,
}) => {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors, isSubmitting },
  } = useForm<CategoriaLigaFormValues>({
    resolver: zodResolver(categoriaLigaSchema),
    defaultValues: initialValues,
  });

  const outrasCategoriasList = outrasCategorias ?? [];
  const generosDisponiveis = Array.from(
    new Set(outrasCategoriasList.map((c) => c.genero).filter(Boolean)),
  ) as string[];
  const niveisDisponiveis = Array.from(
    new Set(outrasCategoriasList.map((c) => c.nivel).filter(Boolean)),
  ) as string[];

  // Atalhos da coluna da direita: cada um é um checkbox cujo estado é DERIVADO da seleção
  // (marcado quando todo o grupo já está selecionado). Marcar une o grupo à seleção; desmarcar
  // remove só o grupo. O que é persistido continua sendo a lista explícita de ids — nenhum
  // atalho vira regra gravada.
  const atalhos: Array<{ key: string; label: string; ids: string[] }> = [
    {
      key: 'todas',
      label: 'Selecionar todas',
      ids: outrasCategoriasList.map((c) => c.id),
    },
    ...generosDisponiveis.map((genero) => ({
      key: `genero-${genero}`,
      label: `Gênero ${GENERO_LABELS[genero] ?? genero}`,
      ids: outrasCategoriasList.filter((c) => c.genero === genero).map((c) => c.id),
    })),
    ...niveisDisponiveis.map((nivel) => ({
      key: `nivel-${nivel}`,
      label: `Nível ${NIVEL_LABELS[nivel] ?? nivel}`,
      ids: outrasCategoriasList.filter((c) => c.nivel === nivel).map((c) => c.id),
    })),
  ];

  const gamesPorSetsAtual = watch('gamesPorSets');
  const tiebreaksAtual = watch('tiebreaks');
  const modeloDisputa = watch('modeloDisputa');
  const numeroAtletas = watch('numeroAtletas');
  const isRodizio = modeloDisputa === 'REI_RAINHA' || modeloDisputa === 'SUPER_8';
  const isDupla = Number(numeroAtletas) === 2;
  const temConfigGrupos =
    modeloDisputa === 'GRUPOS_ELIMINACAO' ||
    modeloDisputa === 'REI_RAINHA';

  // Rodízio (Rei/Rainha, Super 8) é sempre individual: força numeroAtletas=1.
  useEffect(() => {
    if (isRodizio && Number(numeroAtletas) !== 1) {
      setValue('numeroAtletas', 1, { shouldValidate: true, shouldDirty: true });
    }
  }, [isRodizio, numeroAtletas, setValue]);

  // Mantém o tiebreak coerente com o games por set (limpa opção que deixou de ser válida).
  useEffect(() => {
    if (
      tiebreaksAtual != null &&
      gamesPorSetsAtual != null &&
      !getValidTiebreakValues(gamesPorSetsAtual).includes(tiebreaksAtual)
    ) {
      setValue('tiebreaks', undefined, { shouldValidate: true, shouldDirty: true });
    }
  }, [gamesPorSetsAtual, tiebreaksAtual, setValue]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="card mb-3">
      <div className="card-body">
        <div className="row mb-3">
          <div className="col-md-6">
            <label htmlFor="categoriaLiga-nome" className="form-label">Nome</label>
            <input
              id="categoriaLiga-nome"
              className="form-control"
              aria-label="Nome"
              {...register('nome')}
            />
            {errors.nome && <div className="text-danger small">{errors.nome.message}</div>}
          </div>
          <div className="col-md-6">
            <label htmlFor="categoriaLiga-descricao" className="form-label">Descrição</label>
            <input
              id="categoriaLiga-descricao"
              className="form-control"
              aria-label="Descrição"
              {...register('descricao')}
            />
          </div>
        </div>
        <div className="row mb-3">
          <div className="col-md-3">
            <label htmlFor="categoriaLiga-genero" className="form-label">Gênero</label>
            <select id="categoriaLiga-genero" className="form-select" aria-label="Gênero" {...register('genero')}>
              <option value="MASCULINO">Masculino</option>
              <option value="FEMININO">Feminino</option>
              <option value="MISTO">Misto</option>
            </select>
          </div>
          <div className="col-md-3">
            <label htmlFor="categoriaLiga-numeroAtletas" className="form-label">Tipo</label>
            <select
              id="categoriaLiga-numeroAtletas"
              className="form-select"
              aria-label="Tipo"
              disabled={isRodizio}
              {...register('numeroAtletas', {
                setValueAs: (value) => (value === '' ? undefined : Number(value)),
              })}
            >
              <option value="1">Simples</option>
              <option value="2">Dupla</option>
            </select>
            {errors.numeroAtletas && (
              <div className="text-danger small">{errors.numeroAtletas.message}</div>
            )}
          </div>
          <div className="col-md-3">
            <label htmlFor="categoriaLiga-nivel" className="form-label">Nível</label>
            <select id="categoriaLiga-nivel" className="form-select" aria-label="Nível" {...register('nivel')}>
              <option value="">Selecione...</option>
              <option value="A">A</option>
              <option value="B">B</option>
              <option value="C">C</option>
              <option value="D">D</option>
              <option value="E">E</option>
              <option value="INICIANTE">Iniciante</option>
              <option value="INTERMEDIARIO">Intermediário</option>
              <option value="PROFISSIONAL">Profissional</option>
            </select>
            {errors.nivel && <div className="text-danger small">{errors.nivel.message}</div>}
          </div>
          <div className="col-md-3">
            <label htmlFor="categoriaLiga-precoInscricao" className="form-label">Preço inscrição</label>
            <input
              type="number"
              step="0.01"
              id="categoriaLiga-precoInscricao"
              className="form-control"
              aria-label="Preço inscrição"
              {...register('precoInscricao', { valueAsNumber: true })}
            />
            {errors.precoInscricao && (
              <div className="text-danger small">{errors.precoInscricao.message}</div>
            )}
          </div>
        </div>
        <div className="row mb-3">
          <div className="col-md-4">
            <label htmlFor="categoriaLiga-modeloDisputa" className="form-label">Modelo de Disputa</label>
            <select id="categoriaLiga-modeloDisputa" className="form-select" {...register('modeloDisputa')}>
              <option value="">Selecione...</option>
              <option value="ELIMINACAO_SIMPLES">Eliminação Simples</option>
              <option value="ELIMINACAO_DUPLA">Eliminação Dupla</option>
              <option value="GRUPOS_ELIMINACAO">Grupos + Eliminação</option>
              <option value="GRUPO_UNICO">Grupo Único</option>
              <option value="REI_RAINHA">Rei/Rainha da Quadra</option>
              <option value="SUPER_8">Super 8</option>
            </select>
          </div>
          <div className="col-md-4">
            <label htmlFor="categoriaLiga-tipoPontuacao" className="form-label">Tipo de Pontuação</label>
            <select id="categoriaLiga-tipoPontuacao" className="form-select" {...register('tipoPontuacao')}>
              <option value="">Selecione...</option>
              <option value={TipoPontuacao.SETSGAMES}>Sets e Games</option>
            </select>
          </div>
        </div>
        {temConfigGrupos && (
          <div className="row mb-3">
            {modeloDisputa === 'GRUPOS_ELIMINACAO' && (
              <>
                <div className="col-md-4">
                  <label htmlFor="categoriaLiga-quantidadeGrupos" className="form-label">Quantidade de Grupos</label>
                  <input
                    type="number"
                    id="categoriaLiga-quantidadeGrupos"
                    className="form-control"
                    min="1"
                    {...register('quantidadeGrupos', { valueAsNumber: true })}
                  />
                  {errors.quantidadeGrupos && (
                    <div className="text-danger small">{errors.quantidadeGrupos.message}</div>
                  )}
                </div>
                <div className="col-md-4">
                  <label htmlFor="categoriaLiga-classificadosPorGrupo" className="form-label">
                    Classificados por Grupo
                  </label>
                  <input
                    type="number"
                    id="categoriaLiga-classificadosPorGrupo"
                    className="form-control"
                    min="1"
                    {...register('classificadosPorGrupo', { valueAsNumber: true })}
                  />
                  {errors.classificadosPorGrupo && (
                    <div className="text-danger small">{errors.classificadosPorGrupo.message}</div>
                  )}
                </div>
              </>
            )}
            {modeloDisputa === 'REI_RAINHA' && (
              <div className="col-md-4">
                <label htmlFor="categoriaLiga-tamanhoGrupo" className="form-label">Tamanho de Grupo</label>
                <select
                  id="categoriaLiga-tamanhoGrupo"
                  className="form-select"
                  {...register('tamanhoGrupo', { valueAsNumber: true })}
                >
                  <option value="4">4</option>
                  <option value="8">8</option>
                </select>
                {errors.tamanhoGrupo && (
                  <div className="text-danger small">{errors.tamanhoGrupo.message}</div>
                )}
              </div>
            )}
          </div>
        )}
        <div className="row mb-3">
          <div className="col-md-4">
            <label htmlFor="categoriaLiga-numeroSets" className="form-label">Número de Sets</label>
            <select
              id="categoriaLiga-numeroSets"
              className="form-select"
              {...register('numeroSets', {
                setValueAs: (value) => (value === '' ? undefined : Number(value)),
              })}
            >
              <option value="">Selecione...</option>
              <option value={1}>1 Set</option>
              <option value={2}>2 Sets + Super Tiebreak</option>
              <option value={3}>3 Sets</option>
              <option value={4}>4 Sets + Super Tiebreak</option>
              <option value={5}>5 Sets</option>
            </select>
            {errors.numeroSets && <div className="text-danger small">{errors.numeroSets.message}</div>}
          </div>
          <div className="col-md-4">
            <label htmlFor="categoriaLiga-gamesPorSets" className="form-label">Games por Set</label>
            <select
              id="categoriaLiga-gamesPorSets"
              className="form-select"
              {...register('gamesPorSets', {
                setValueAs: (value) => (value === '' ? undefined : Number(value)),
              })}
            >
              <option value="">Selecione...</option>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((num) => (
                <option key={num} value={num}>
                  {num} {num === 1 ? 'Game' : 'Games'}
                </option>
              ))}
            </select>
            {errors.gamesPorSets && (
              <div className="text-danger small">{errors.gamesPorSets.message}</div>
            )}
          </div>
          <div className="col-md-4">
            <label htmlFor="categoriaLiga-tiebreaks" className="form-label">Tiebreak</label>
            <select
              id="categoriaLiga-tiebreaks"
              className="form-select"
              disabled={!gamesPorSetsAtual}
              {...register('tiebreaks', {
                setValueAs: (value) => {
                  if (value === '' || value === 'null') return undefined;
                  const num = Number(value);
                  return isNaN(num) ? undefined : num;
                },
              })}
            >
              {getTiebreakOptions(gamesPorSetsAtual).map((option, index) => (
                <option key={index} value={option.value}>{option.label}</option>
              ))}
            </select>
            {errors.tiebreaks && <div className="text-danger small">{errors.tiebreaks.message}</div>}
          </div>
        </div>
        <div className="row mb-3">
          <div className="col-md-4">
            <label htmlFor="categoriaLiga-idadeInicial" className="form-label">Idade Inicial</label>
            <input
              type="number"
              id="categoriaLiga-idadeInicial"
              className="form-control"
              {...register('idadeInicial', { valueAsNumber: true })}
            />
          </div>
          <div className="col-md-4">
            <label htmlFor="categoriaLiga-idadeFinal" className="form-label">Idade Final</label>
            <input
              type="number"
              id="categoriaLiga-idadeFinal"
              className="form-control"
              {...register('idadeFinal', { valueAsNumber: true })}
            />
          </div>
          {isDupla && (
            <div className="col-md-4">
              <label htmlFor="categoriaLiga-idadeCombinada" className="form-label">Idade Combinada</label>
              <input
                type="number"
                id="categoriaLiga-idadeCombinada"
                className="form-control"
                {...register('idadeCombinada', { valueAsNumber: true })}
              />
            </div>
          )}
        </div>
        <div className="row mb-3">
          <div className="col-12">
            <label className="form-label d-block">
              Jogadores desta categoria também podem jogar em
            </label>
            <Controller
              control={control}
              name="combinaveisIds"
              render={({ field }) => {
                const selecionados = field.value ?? [];
                const toggle = (id: string) => {
                  if (selecionados.includes(id)) {
                    field.onChange(selecionados.filter((x) => x !== id));
                  } else {
                    field.onChange([...selecionados, id]);
                  }
                };
                return (
                  <div className="row">
                    <div className="col-md-8">
                      {outrasCategoriasList.map((categoria) => (
                        <div className="form-check" key={categoria.id}>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id={`categoriaLiga-combinavel-${categoria.id}`}
                            checked={selecionados.includes(categoria.id)}
                            onChange={() => toggle(categoria.id)}
                          />
                          <label
                            className="form-check-label"
                            htmlFor={`categoriaLiga-combinavel-${categoria.id}`}
                          >
                            {categoria.nome}
                          </label>
                        </div>
                      ))}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label d-block">Atalhos</label>
                      <div className="d-flex flex-column gap-1">
                        {atalhos.map((atalho) => {
                          const marcado =
                            atalho.ids.length > 0 &&
                            atalho.ids.every((id) => selecionados.includes(id));
                          return (
                            <div className="form-check" key={atalho.key}>
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id={`categoriaLiga-atalho-${atalho.key}`}
                                checked={marcado}
                                onChange={() =>
                                  field.onChange(
                                    marcado
                                      ? // Desmarcar o atalho remove APENAS o grupo dele; as demais
                                        // seleções manuais do organizador continuam intactas.
                                        selecionados.filter(
                                          (id) => !atalho.ids.includes(id),
                                        )
                                      : // Marcar UNE o grupo à seleção atual (sem duplicar ids).
                                        Array.from(
                                          new Set([...selecionados, ...atalho.ids]),
                                        ),
                                  )
                                }
                              />
                              <label
                                className="form-check-label"
                                htmlFor={`categoriaLiga-atalho-${atalho.key}`}
                              >
                                {atalho.label}
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              }}
            />
          </div>
        </div>
        <div className="d-flex gap-2 justify-content-end">
          <button type="button" className="btn btn-secondary" onClick={onCancel}>
            Cancelar
          </button>
          <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
            Salvar
          </button>
        </div>
      </div>
    </form>
  );
};

export default CategoriaLigaForm;
